// File: frontend/src/pages/team/TeamPage.tsx
// src/pages/team/TeamPage.tsx

import React, { useEffect, useState, useCallback, useRef } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { RiZoomInLine, RiCloseLine } from "react-icons/ri";
import "./TeamPage.css";
import { TeamModel, getTeamDisplayName, getTeamUrlSlug } from "../../models/TeamModel";
import { teamService } from "../../services/teamService";
import { postTeamEntry, fetchEntriesByTeamId } from "../../services/clubEntryService";
import { getCountryNameTr } from "../../utils/countryUtils";
import { useTeamEntries } from "../../hooks/useTeamEntries";
import { useCollapsedOldEntries, CollapseEntryFetcher } from "../../hooks/useCollapsedOldEntries";
import { useDocumentMeta } from "../../hooks/useDocumentMeta";
import { useUrlPage, pageSuffixes, useIsEntryDeepLink, useFocusEntryId, useExitFocus } from "../../hooks/useUrlPage";
import { useFocusedEntry } from "../../hooks/useFocusedEntry";
import { Pagination } from "../../utils";
import { absoluteUrl, clubUrl, teamUrl, withCurrentQueryAndHash } from "../../utils/canonicalUrl";
import { buildTopicShareUrl } from "../../utils/shareUtils";
import { SpinnerLoading } from "../../utils";
import { EntryRow } from "../../components/entries/EntryRow";
import { CollapsedEntriesBar } from "../../components/entries/CollapsedEntriesBar";
// Sitewide pins: admin-pinned entry shown at the top of the topic page
import { PinnedEntryBlock } from "../../components/entries/PinnedEntryBlock";
import { EntryToolbar, EntrySortOption } from "../../components/entries/EntryToolbar";
import { EntryPostForm } from "../../components/entries/EntryPostForm";
import { usePreferences } from "../../context/PreferencesContext";
import { useAuth } from "../../context/AuthContext";
import { ENTRIES_PER_PAGE } from "../../constants";
import {
  FaFutbol, FaBasketballBall, FaVolleyballBall,
  FaFootballBall, FaBaseballBall, FaHockeyPuck,
  FaArrowLeft, FaShieldAlt, FaChevronRight,
  FaMale, FaFemale,
} from "react-icons/fa";
import { MdSportsHandball } from "react-icons/md";
import { RedirectError } from "../../services/apiClient";

interface TeamPageProps {
  // URL param from the canonical single-segment route /takim/:teamSlug,
  // e.g. "galatasaray-futbol--372".
  //
  // clubSlug is only set when the page was reached through the LEGACY
  // two-segment route /takim/:clubSlug/:teamSlug. In that case the backend
  // answers 301 and this page navigates to the canonical address, so the
  // legacy shape never renders for more than a moment.
  clubSlug?: string;
  teamSlug: string;
  // Called after a new entry is successfully posted so App.tsx can
  // refresh the team list and re-sort it (e.g. move this team to top).
  onEntryPosted?: () => void;
  // Called when the mobile back button is tapped to return to the list pane
  onMobileBack?: () => void;
}

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:8080";

const resolveUrl = (url: string | null): string | null => {
  if (!url) return null;
  return url.startsWith("http") ? url : `${API_BASE}${url}`;
};

const SportIcon: React.FC<{ sportType: string; size?: number; className?: string }> = ({ sportType, size = 13, className }) => {
  const p = { size, className };
  switch (sportType) {
    case "FOOTBALL":          return <FaFutbol {...p} />;
    case "BASKETBALL":        return <FaBasketballBall {...p} />;
    case "VOLLEYBALL":        return <FaVolleyballBall {...p} />;
    case "AMERICAN_FOOTBALL": return <FaFootballBall {...p} />;
    case "BASEBALL":          return <FaBaseballBall {...p} />;
    case "ICE_HOCKEY":        return <FaHockeyPuck {...p} />;
    case "HANDBALL":          return <MdSportsHandball {...p} />;
    default:                  return <FaFutbol {...p} />;
  }
};

const SPORT_LABELS: Record<string, string> = {
  FOOTBALL:          "Futbol",
  BASKETBALL:        "Basketbol",
  VOLLEYBALL:        "Voleybol",
  AMERICAN_FOOTBALL: "Amerikan Futbolu",
  BASEBALL:          "Beyzbol",
  ICE_HOCKEY:        "Buz Hokeyi",
  HANDBALL:          "Hentbol",
};

/**
 * Number of sport-ball icons rendered into the tiled banner background.
 * Enough to fill a full-width banner across multiple rows; any icons beyond
 * the visible area are clipped by the banner's overflow:hidden.
 */
const PATTERN_ICON_COUNT = 140;

/**
 * Builds a CSS gradient string from up to three parent-club brand colors.
 * Mirrors ClubPage.buildBannerGradient so a team page belongs to the same
 * visual family as its club. A slightly steeper angle (120deg vs the club's
 * 135deg) plus the tiled sport-ball background (added in CSS/JSX)
 * make the team banner instantly distinguishable from a club banner.
 * When no colors are defined, falls back to a dark neutral grey.
 * Club colors are used as-is so actual brand identity is preserved.
 */
const buildBannerGradient = (c1: string | null, c2: string | null, c3: string | null): string => {
  const colors = [c1, c2, c3].filter(Boolean) as string[];
  if (colors.length === 0) return "linear-gradient(120deg, #2c2c2c 0%, #1a1a1a 100%)";
  if (colors.length === 1) return `linear-gradient(120deg, ${colors[0]} 0%, ${colors[0]}99 100%)`;
  if (colors.length === 2) return `linear-gradient(120deg, ${colors[0]} 0%, ${colors[1]} 100%)`;
  return `linear-gradient(120deg, ${colors[0]} 0%, ${colors[1]} 50%, ${colors[2]} 100%)`;
};

export const TeamPage: React.FC<TeamPageProps> = ({ clubSlug, teamSlug, onEntryPosted, onMobileBack }) => {
  const { t } = useTranslation();
  const { preferences } = usePreferences();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const [team, setTeam]       = useState<TeamModel | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);

  // Club logo shown in the team banner: popover open state, and whether the
  // logo URL 404s/fails to load (stale or wrong link) — in that case we fall
  // back to the shield icon instead of a broken-image glyph.
  // Mirrors the person page avatar popover (PersonPage.tsx) one-to-one.
  const [logoOpen, setLogoOpen]     = useState(false);
  const [logoFailed, setLogoFailed] = useState(false);
  // Screen coordinates the popover renders at — computed from the logo's own
  // position when opened, so it appears right next to the logo it enlarges
  // instead of dead-center of the screen. Rendered via a portal (see below),
  // so this is relative to the viewport, not this component.
  const logoBtnRef = useRef<HTMLButtonElement>(null);
  const [popoverPos, setPopoverPos] = useState<{ top: number; left: number } | null>(null);

  // Mirrors the CSS sizing on .team-page__logo-popover img — kept in sync
  // manually since the clamp math below needs the same numbers CSS uses.
  // Club logos are square 150x150 sources, so the box is square too.
  const POPOVER_W = 150;
  const POPOVER_H = 150;
  const POPOVER_MARGIN = 16;

  const openLogo = () => {
    // Mobile: always center — anchoring next to a tiny header logo on a
    // narrow screen has nowhere good to go and just looks broken. Desktop
    // keeps the "pop out right next to the logo" behavior.
    const isMobile = window.innerWidth <= 768;
    const rect = isMobile ? undefined : logoBtnRef.current?.getBoundingClientRect();
    const effW = Math.min(window.innerWidth * 0.55, POPOVER_W);
    const effH = Math.min(window.innerHeight * 0.45, POPOVER_H);
    if (rect) {
      let left = rect.left;
      let top = rect.bottom + 10;
      // Keep it on-screen: slide left if it would overflow the right edge...
      if (left + effW + POPOVER_MARGIN > window.innerWidth) {
        left = Math.max(POPOVER_MARGIN, window.innerWidth - effW - POPOVER_MARGIN);
      }
      // ...and flip above the logo if there isn't enough room below it.
      if (top + effH + POPOVER_MARGIN > window.innerHeight) {
        top = Math.max(POPOVER_MARGIN, rect.top - effH - 10);
      }
      setPopoverPos({ top, left });
    } else {
      setPopoverPos(null);
    }
    setLogoOpen(true);
  };

  // ESC closes the logo popover, same convention as other overlays.
  useEffect(() => {
    if (!logoOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setLogoOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [logoOpen]);

  // A different team was opened in the left pane while the popover was up —
  // close it and drop the stale failure flag so the new logo gets a fresh try.
  useEffect(() => {
    setLogoOpen(false);
    setLogoFailed(false);
  }, [teamSlug]);

  // Sets document.title / meta description / canonical / OG tags for this
  // team's page once it has loaded. Falls back to the site-wide defaults
  // (see useDocumentMeta) while loading or on load failure.
  const teamDisplayName = team ? getTeamDisplayName(team) : null;
  const teamSportLabel  = team ? (SPORT_LABELS[team.sportType] ?? team.sportType) : null;
  // Entry page number lives in the URL ("?sayfa=2") rather than in state, so a
  // page can be linked to and shared, survives a refresh, and gives each page
  // its own indexable address.
  const { page: urlPage, setPage: setUrlPage } = useUrlPage();

  useDocumentMeta({
    title: teamDisplayName ? `${teamDisplayName}${pageSuffixes(urlPage).titleSuffix} - Üç Korner Bir Penaltı` : undefined,
    description: (teamDisplayName && teamSportLabel)
      ? `${teamDisplayName} (${teamSportLabel}) hakkında yazılan tanımları oku, sen de yorumunu ekle, tartış ve oyla.`
      : undefined,
    canonicalUrl: team ? absoluteUrl(teamUrl(team.clubSlug, getTeamUrlSlug(team), team.id)) + pageSuffixes(urlPage).urlSuffix : undefined,
    imageUrl: team?.clubLogoUrl || undefined,
  });

  // Toolbar state: keyword search, filters, and sort for the entry list
  const [sort, setSort]               = useState<EntrySortOption>("oldest");
  const [filterToday, setFilterToday] = useState(false);
  const [rookieOnly, setRookieOnly]   = useState(false);
  const [keyword, setKeyword]         = useState("");

  // Anchor used by the toolbar "sayfa sonu" button to scroll down to the entry form
  const bottomRef = useRef<HTMLDivElement>(null);
  const handleScrollToBottom = () => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const showRookieEntries = preferences?.showRookieEntries !== false;
  const entriesPerPage    = preferences?.entriesPerPage ?? ENTRIES_PER_PAGE;
  const teamId            = team?.id ?? null;

  // Sort oldest-first so the newest entry lands at the bottom, just above the
  // input form — matching the PersonPage and match-page layout.
  const {
    entries, loading: loadingEntries, error: entriesError,
    totalItems, currentPage, totalPages,
    isLoadingMore, loadEntries, loadMoreEntries, updateEntries,
  } = useTeamEntries(teamId, showRookieEntries, sort, entriesPerPage, filterToday, rookieOnly, keyword, urlPage);

  // Only active in the default view: oldest sort, no filters, no keyword.
  // An address that names a page ("?sayfa=4") or an entry ("#entry-1499") is a
  // deep link, and the collapsed view cannot serve it: it renders only today's
  // entries and hides the pagination controls, so the requested page was
  // fetched and never shown and the anchored entry was never in the DOM for
  // the scroll to find. An explicit request wins over the default view.
  const isDeepLink = useIsEntryDeepLink();
  const collapseEligible =
    sort === "oldest" && !filterToday && !rookieOnly && !keyword.trim() && !isDeepLink;

  // Fetcher used by the collapse hook — always oldest sort, viewer's rookie preference
  const collapseFetcher = useCallback<CollapseEntryFetcher>(async (page, size, fToday) => {
    if (teamId === null) throw new Error("No team selected");
    const res = await fetchEntriesByTeamId(teamId, page, size, showRookieEntries, "oldest", fToday, false, "");
    return { entries: res.entries, totalItems: res.totalItems, totalPages: res.totalPages };
  }, [teamId, showRookieEntries]);

  const collapse = useCollapsedOldEntries(teamId, collapseEligible, entriesPerPage, collapseFetcher);

  // Permalink view: "?tanim=747" shows that one tanım framed by "{n} entry
  // daha" bars instead of dropping the reader into the middle of a page. It
  // reuses the collapse fetcher, so nothing new has to be wired per domain.
  const focusEntryId = useFocusEntryId();
  const exitFocus = useExitFocus();
  const focus = useFocusedEntry(teamId, focusEntryId, entriesPerPage, collapseFetcher);
  const handleExitFocus = useCallback(() => {
    if (focus.entry) exitFocus(focus.page, focus.entry.id);
  }, [exitFocus, focus.entry, focus.page]);

  // Entries actually rendered: collapsed view (today's + revealed older) or the normal list
  // Focused permalink view wins over both the collapsed view and the normal
  // list: the reader asked for one specific tanım.
  const displayEntries = focus.active
    ? (focus.entry ? [focus.entry] : [])
    : (collapse.active ? collapse.entries : entries);

  // Apply local mutations (vote, delete, new post) to both lists so the visible one stays correct
  const applyEntriesUpdate = useCallback((fn: (prev: typeof entries) => typeof entries) => {
    updateEntries(fn);
    collapse.updateEntries(fn);
  }, [updateEntries, collapse.updateEntries]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Ticket 1c: the header (club logo, team name, share link) comes from this
    // request while the entry list comes from a second, independent one. Two
    // quick clicks in the left pane leave two team requests in flight, and
    // without this flag the slower one wins simply by arriving last — which is
    // how the page ended up showing one team's header above another team's
    // entries. React runs the cleanup before re-running the effect, so the
    // superseded request is marked stale the instant a new slug is requested.
    let cancelled = false;

    setLoading(true);
    setError(null);
    setTeam(null);
    (clubSlug
      ? teamService.getTeamBySlug(clubSlug, teamSlug)   // legacy address → 301
      : teamService.getTeamBySegment(teamSlug))         // canonical single segment
      .then((loaded) => {
        if (cancelled) return;
        setTeam(loaded);
      })
      .catch((err) => {
        if (cancelled) return;
        // The team was merged — navigate to the surviving team page
        // Covers both a merged-away topic (410) and a non-canonical
        // slug (301) — both mean "continue at redirectPath".
        if (err instanceof RedirectError) {
          // Keep "?sayfa=" and "#entry-": this fires for every legacy or
          // non-canonical slug, which is exactly what an older shared deep
          // link is. Passing the path alone dropped the page and the anchor,
          // so the reader landed on page 1 with nothing to scroll to.
          navigate(withCurrentQueryAndHash(err.redirectPath), { replace: true });
          return;
        }
        setError(t("team_load_error", "Takım bilgileri yüklenemedi."));
      })
      .finally(() => {
        // Ticket 1c: only the request that is still current may clear the
        // spinner — otherwise the newer team renders with stale header state.
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [clubSlug, teamSlug, t, navigate]);

  const handlePostEntry = useCallback(async (content: string) => {
    if (!teamId) return;
    const newEntry = await postTeamEntry(content, teamId);
    // Append to end so the new entry appears just above the input form
    applyEntriesUpdate(prev => [...prev, newEntry]);
    loadEntries(1);
    // Notify App.tsx so it can refresh the team list and re-sort it
    onEntryPosted?.();
  }, [teamId, applyEntriesUpdate, loadEntries, onEntryPosted]);

  const handleDeleteEntry = (entryId: number) => {
    applyEntriesUpdate(prev => prev.filter(e => e.id !== entryId));
  };

  if (loading) return <SpinnerLoading />;
  if (error)   return <div className="team-page__error">{error}</div>;
  if (!team)   return <div className="team-page__not-found">{t("team_not_found", "Takım bulunamadı.")}</div>;

  const logoSrc     = resolveUrl(team.clubLogoUrl);
  // Only offer the enlarge interaction while the logo actually renders
  const hasLogo     = Boolean(logoSrc) && !logoFailed;
  const isFlagLogo  = team.clubLogoUrl?.includes("flags/") ?? false;
  const displayName = getTeamDisplayName(team);
  const sportLabel  = SPORT_LABELS[team.sportType] ?? team.sportType;
  const bannerStyle = { background: buildBannerGradient(team.clubColor1, team.clubColor2, team.clubColor3) };

  // Gender chip — silhouette figure icons (same as MatchRow), label always shown
  const genderChip: { icon: React.ReactNode; label: string } | null =
    team.gender === "FEMALE" ? { icon: <FaFemale size={13} />, label: t("match_gender_female", "Kadın") }
    : team.gender === "MIXED" ? { icon: <FaMale size={13} />, label: t("match_gender_mixed", "Karışık") }
    : { icon: <FaMale size={13} />, label: t("match_gender_male", "Erkek") };

  // teamCode label — shown in uppercase when set (e.g. "U19", "1B", "PAF")
  const teamCodeLabel = team.teamCode ? team.teamCode.toUpperCase() : null;

  // Bottom meta chips: only city · country (sport shown in badge, gender shown under badge)
  const metaParts = [
    team.clubCity,
    team.clubCountry ? getCountryNameTr(team.clubCountry) : null,
  ].filter(Boolean) as string[];

  return (
    <div className="team-page">
      {/* Mobile back button — hidden on desktop via CSS, visible on mobile */}
      {onMobileBack && (
        <button className="mobile-back-btn" onClick={onMobileBack} aria-label="Geri">
          <FaArrowLeft size={14} />
          {t("mobile_back", "Geri")}
        </button>
      )}

      {/* Full-width colored banner — shares the parent club's brand colors so the
          team belongs to the same visual family, but stays instantly distinct from
          a club page via two team-only signals:
            1) the whole background tiled with the sport's ball icon, so the sport
               is obvious everywhere at a glance,
            2) a sport-icon "TAKIM" badge (vs the club's plain KULÜP pill).
          Top row: logo (left) | team name centred (flex:1) | sport TAKIM badge (right).
          Bottom row: sport · gender · city · country meta chips.
          Club strip: a single prominent "back to parent club" button. */}
      <div className="team-page__banner" style={bannerStyle}>

        {/* Tiled sport-ball background — fills the entire banner with repeated
            sport-ball icons so the sport reads instantly across the whole surface.
            Replaces the old single corner watermark + diagonal stripe overlay.
            Sport-aware: reuses the same SportIcon shown elsewhere. Decorative only. */}
        <div className="team-page__pattern" aria-hidden="true">
          {Array.from({ length: PATTERN_ICON_COUNT }).map((_, i) => (
            <SportIcon
              key={i}
              sportType={team.sportType}
              size={42}
              className="team-page__pattern-icon"
            />
          ))}
        </div>

        {/* Top row */}
        <div className="team-page__banner-top">
          <div className="team-page__banner-logo">
            {hasLogo ? (
              <button
                ref={logoBtnRef}
                type="button"
                className="team-page__banner-logo-btn"
                onClick={openLogo}
                title={t("team_view_logo", "Logoyu büyüt")}
                aria-label={t("team_view_logo", "Logoyu büyüt")}
              >
                <img
                  src={logoSrc as string}
                  alt={team.clubShortName}
                  className={isFlagLogo ? "flag-img" : "logo-img"}
                  onError={() => setLogoFailed(true)}
                />
                {/* Hover/focus affordance: dim overlay + magnifier, same
                    convention as the person page avatar. */}
                <span className="team-page__banner-logo-overlay" aria-hidden="true">
                  <RiZoomInLine />
                </span>
              </button>
            ) : (
              <FaShieldAlt size={40} className="team-page__banner-icon" />
            )}
          </div>
          <h1 className="team-page__name">{displayName}</h1>
          {/* Sport TAKIM badge — sport icon + sport label + optional teamCode + "TAKIM" word.
              The sport icon prefix and square shape set it apart from the club pill. */}
          <div className="team-page__badge-col">
            <span className="team-page__type-label">
              <SportIcon sportType={team.sportType} size={12} />
              <span>{sportLabel}{teamCodeLabel ? ` ${teamCodeLabel}` : ""} {t("team_type_badge", "TAKIMI")}</span>
            </span>
            {/* Gender icon shown below the badge */}
            {genderChip && (
              <span className="team-page__gender-under-badge">
                {genderChip.icon}
                {" "}{genderChip.label}
              </span>
            )}
            {/* teamCode shown below gender when set (e.g. U19, 1B) */}
            {teamCodeLabel && (
              <span className="team-page__gender-under-badge">
                {teamCodeLabel}
              </span>
            )}
          </div>
        </div>

        {/* Bottom info row: sport · teamCode · city · country (gender shown under badge, not here) */}
        {metaParts.length > 0 && (
          <div className="team-page__banner-bottom">
            {metaParts.map((part, i) => (
              <span key={i} className="team-page__meta-chip">{part}</span>
            ))}
          </div>
        )}

        {/* Club strip — single prominent back-to-parent-club button.
            This replaces the club page's multi-team strip, reinforcing that a team
            is a branch *of* a club rather than the club itself. */}
        <div className="team-page__club-strip">
          <span className="team-page__club-strip-label">{t("team_club_strip_label", "Kulüp")}:</span>
          <button
            className="team-page__club-btn"
            onClick={() => navigate(clubUrl(team.clubSlug, team.clubId))}
            title={t("team_back_to_club", "Kulüp sayfasına git")}
          >
            <FaShieldAlt size={12} />
            <span>{team.clubShortName}</span>
            <FaChevronRight size={10} className="team-page__club-btn-arrow" />
          </button>
        </div>
      </div>

      {logoOpen && hasLogo && ReactDOM.createPortal(
        // Deliberately NOT the full-screen ImageLightbox used for entry
        // uploads — a club crest only needs a modest zoom-in next to the
        // logo it belongs to, not a full takeover of the screen.
        //
        // Portalled to document.body (same pattern as ImageLightbox /
        // ConfirmModal): rendering this inline would make `position: fixed`
        // resolve against whatever ancestor happens to have a `transform`
        // on it (route-transition wrappers, etc.) instead of the real
        // viewport, which is what would push it into a random corner.
        <div
          className="team-page__logo-popover-backdrop"
          onClick={() => setLogoOpen(false)}
        >
          <div
            className="team-page__logo-popover"
            // Falls back to the CSS default (viewport-centered) if we
            // somehow don't have a measured position yet.
            style={
              popoverPos
                ? { top: popoverPos.top, left: popoverPos.left, transform: "none" }
                : undefined
            }
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="team-page__logo-popover-close"
              onClick={() => setLogoOpen(false)}
              aria-label={t("close", "Kapat")}
            >
              <RiCloseLine />
            </button>
            <img src={logoSrc as string} alt={team.clubShortName} />
          </div>
        </div>,
        document.body
      )}

      {/* Toolbar: search, filters, sort, scroll-to-bottom — same behaviour as match pages */}
      <EntryToolbar
        sort={sort}
        onSortChange={setSort}
        filterToday={filterToday}
        onFilterTodayChange={setFilterToday}
        rookieOnly={rookieOnly}
        onRookieOnlyChange={setRookieOnly}
        keyword={keyword}
        onKeywordChange={setKeyword}
        onScrollToBottom={handleScrollToBottom}
        shareUrl={buildTopicShareUrl(teamUrl(team.clubSlug, getTeamUrlSlug(team), team.id))}
        shareTitle={displayName}
      />

      {/* Entries — oldest first so the newest lands just above the input form */}
      <div className="team-page__entries">
        {focus.loading || collapse.checking || (loadingEntries && !collapse.active && !focus.active) ? (
          <SpinnerLoading />
        ) : entriesError && !collapse.active ? (
          <p className="team-page__empty" style={{ color: "var(--color-error)" }}>{entriesError}</p>
        ) : displayEntries.length === 0 ? (
          <p className="team-page__empty">{t("team_no_entries", "Henüz tanım yok.")}</p>
        ) : (
          <>
            {/* Sözlük-convention bar: older entries are collapsed, clicking reveals the previous chunk */}
            {/* Permalink view: the shared tanım is framed by a "{n} entry daha"
                bar on each side. Clicking either one leaves the focused view
                for the full page this entry sits on, with the entry still on
                screen — the bars are the way back into the topic. */}
            {focus.active && (
              <CollapsedEntriesBar
                hiddenCount={focus.hiddenAbove}
                isLoading={false}
                onClick={handleExitFocus}
              />
            )}
            {collapse.active && (
              <CollapsedEntriesBar
                hiddenCount={collapse.hiddenCount}
                isLoading={collapse.isLoadingOlder}
                onClick={collapse.loadOlderEntries}
              />
            )}
            {/* Admin-pinned entry of this topic — rendered above the regular list */}
            <PinnedEntryBlock type="TEAM" targetId={teamId} />
            {displayEntries.map(entry => (
              <EntryRow
                key={entry.id}
                entry={entry}
                onDelete={handleDeleteEntry}
                onVoteUpdate={(entryId, updates) =>
                  applyEntriesUpdate(prev => prev.map(e => e.id === entryId ? { ...e, ...updates } : e))
                }
              />
            ))}
            {focus.active && (
              <CollapsedEntriesBar
                hiddenCount={focus.hiddenBelow}
                isLoading={false}
                onClick={handleExitFocus}
              />
            )}
            {/* Page navigation. Reads and writes the "?sayfa=" URL parameter, so the
                reader can link to a page and the back button returns to it. */}
            {!focus.active && totalPages > 1 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalItems}
                paginate={setUrlPage}
              />
            )}
            {!focus.active && !collapse.active && currentPage < totalPages && (
              <button className="team-page__load-more" onClick={loadMoreEntries} disabled={isLoadingMore}>
                {isLoadingMore
                  ? t("load_more_loading", "yükleniyor...")
                  : t("load_more_entries", "{{count}} tanım daha", { count: totalItems - entries.length })}
              </button>
            )}
          </>
        )}
      </div>

      {/* Entry post form — always at the bottom, below all entries */}
      {/* Anchor for the toolbar "sayfa sonu" scroll target */}
      <div ref={bottomRef} />

      {isAuthenticated ? (
        <EntryPostForm
          selectedMatch={null}
          clubId={null}
          teamId={teamId}
          onPostEntry={handlePostEntry}
          isLocked={team?.isLocked ?? false}
          lockReason={team?.lockReason}
        />
      ) : (
        <p className="login-prompt-message">{t("login_to_post_entry")}</p>
      )}
    </div>
  );
};